import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Link as LinkIcon, MoreHorizontal, Star, StarOff } from 'lucide-react';
import { Menu, toast } from '@vatman/ui';
import { useIsFavorite, useToggleFavorite, type FavoriteKind } from '../lib/favorites';

/*
 * The "⋯" of a row in the menu (docs/navigation-v2-plan.md), also opened by a right click on the
 * row: open in a new tab, copy the link, pin to favorites, and what the row adds (rename, create
 * inside).
 */

export interface RowAction {
  icon: ReactNode;
  label: string;
  run: () => void;
  danger?: boolean;
}

/** The row's menu state, and the right click that opens it. */
export function useRowMenu() {
  const [open, setOpen] = useState(false);
  return {
    open,
    setOpen,
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      setOpen(true);
    },
  };
}

export function NavRowMenu({
  href,
  fav,
  actions = [],
  open,
  onOpenChange,
}: {
  href: string;
  fav?: { kind: FavoriteKind; id: string };
  actions?: RowAction[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const starred = useIsFavorite(fav?.kind ?? 'board', fav?.id);
  const toggle = useToggleFavorite();
  const copy = () => {
    void navigator.clipboard.writeText(new URL(href, location.origin).toString()).then(
      () => toast(t('nav.linkCopied')),
      () => toast(t('errors.internal')),
    );
  };
  return (
    <Menu.Root open={open} onOpenChange={onOpenChange}>
      <Menu.Trigger asChild>
        <button
          type="button"
          className="nav-row-more"
          data-open={open}
          aria-label={t('common.more')}
          onClick={(e) => {
            // Inside the row's link: the menu, not the page.
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <MoreHorizontal size={14} />
        </button>
      </Menu.Trigger>
      <Menu.Content align="start">
        {/* The menu shows in a portal, but its events still reach the row's link in React: a
            chosen item must not also open the row. */}
        <div style={{ display: 'contents' }} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
          <Menu.Item icon={<ExternalLink size={16} />} onSelect={() => window.open(href, '_blank', 'noopener')}>
            {t('nav.openNewTab')}
          </Menu.Item>
          <Menu.Item icon={<LinkIcon size={16} />} onSelect={copy}>
            {t('nav.copyLink')}
          </Menu.Item>
          {fav && (
            <Menu.Item icon={starred ? <StarOff size={16} /> : <Star size={16} />} onSelect={() => void toggle(fav.kind, fav.id, !starred)}>
              {t(starred ? 'favorites.remove' : 'favorites.add')}
            </Menu.Item>
          )}
          {actions.length > 0 && <Menu.Separator />}
          {actions.map((a) => (
            <Menu.Item key={a.label} icon={a.icon} danger={a.danger} onSelect={a.run}>
              {a.label}
            </Menu.Item>
          ))}
        </div>
      </Menu.Content>
    </Menu.Root>
  );
}
